import { CostToCureAnalysisResult } from '@aloft/toolkit-shared';
import { Block, Flex, Typography } from '@aloft/toolkit-sui';

import { ImageCarousel } from './ImageCarousel.tsx';

type CostToCureResultWithContext = CostToCureAnalysisResult & {
  imageURLs?: string[];
  appraisal?: {
    costToCureAmount?: number;
    commentary?: string;
    conditionRating?: string;
  };
};

const formatDollars = (amount?: number) =>
  amount == null
    ? 'Not provided'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export const CostToCureResults = ({ data }: { data: CostToCureResultWithContext }) => {
  const findings = data.content.findings;
  const hasFindings = findings.length > 0;
  const appraisal = data.appraisal ?? {};
  const imageURLs = data.imageURLs ?? [];

  return (
    <Flex flexDirection="column" gap="16px">
      <Block padding="16px" borderRadius="8px" backgroundColor="surface2">
        <Flex flexDirection="column" gap="12px">
          <Typography variant="h3">Provided Appraisal Context</Typography>
          <Flex gap="12px" flexWrap="wrap">
            <Block padding="10px 12px" borderRadius="6px" backgroundColor="surface1">
              <Typography variant="body2" color="textSecondary">
                Cost-to-Cure
              </Typography>
              <Typography variant="h4">{formatDollars(appraisal.costToCureAmount)}</Typography>
            </Block>
            <Block padding="10px 12px" borderRadius="6px" backgroundColor="surface1">
              <Typography variant="body2" color="textSecondary">
                Condition Rating
              </Typography>
              <Typography variant="h4">{appraisal.conditionRating ?? 'Not provided'}</Typography>
            </Block>
          </Flex>
          <Block padding="12px" borderRadius="6px" backgroundColor="surface1">
            <Typography variant="body2" color="textSecondary">
              Commentary
            </Typography>
            <Typography variant="body2">{appraisal.commentary ?? 'Not provided'}</Typography>
          </Block>
        </Flex>
      </Block>

      <Block
        padding="16px"
        borderRadius="8px"
        backgroundColor={hasFindings ? 'alertWarningLight' : 'surface2'}
      >
        <Flex flexDirection="column" gap="6px">
          <Typography variant="h3">
            {hasFindings ? `${findings.length} finding${findings.length === 1 ? '' : 's'}` : 'No findings'}
          </Typography>
          <Typography variant="body1">{data.content.summary}</Typography>
        </Flex>
      </Block>

      {hasFindings && (
        <Flex flexDirection="column" gap="12px">
          {findings.map((finding, index) => (
            <Block
              key={`${finding.category}-${index}`}
              padding="16px"
              borderRadius="8px"
              backgroundColor="surface1"
              border="1px solid #e0e0e0"
            >
              <Flex flexDirection="column" gap="8px">
                <Typography variant="h4">{finding.category}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {finding.description}
                </Typography>
              </Flex>
            </Block>
          ))}
        </Flex>
      )}

      {imageURLs.length > 0 && (
        <Flex flexDirection="column" gap="8px">
          <Typography variant="h3">Report Photos</Typography>
          <ImageCarousel urls={imageURLs} />
        </Flex>
      )}
    </Flex>
  );
};
